/**
 * WalletTimeline — card del Dashboard con sparkline del balance + toggle
 * 7d / 30d + delta desde el inicio del rango.
 *
 *  - Fetchea /api/wallet/history. KV-cachea mientras no se force-refreshee
 *    (el backend ya pagina el journal con caching por characterId+page).
 *  - El "current balance" del card hereda del endpoint principal si está
 *    en cache (la query wallet-balance vive en el Dashboard).
 *  - Sin input del user: el default es 7d.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatISK, formatISKShort } from '../api'
import { LastUpdated } from './LastUpdated'
import { RefreshButton } from './RefreshButton'
import { LoadingSkeleton } from './LoadingSkeleton'
import { useForceRefetch } from '../hooks/useForceRefetch'
import { WalletSparkline } from './WalletSparkline'

type Range = 7 | 30

const RANGES: Array<{ days: Range; label: string }> = [
  { days: 7, label: '7d' },
  { days: 30, label: '30d' },
]

export function WalletTimeline() {
  const [range, setRange] = useState<Range>(7)
  const { key: forceRefetchKey, force, bump } = useForceRefetch()

  const { data, isLoading, dataUpdatedAt, isFetching } = useQuery({
    queryKey: ['wallet-history', range, forceRefetchKey],
    queryFn: () => api.walletHistory(range, force),
  })

  const positive = (data?.change ?? 0) > 0
  const negative = (data?.change ?? 0) < 0
  const flat = data && Math.abs(data.change_pct) < 0.1
  const deltaColor = flat
    ? 'text-eve-muted'
    : positive
      ? 'text-eve-success'
      : 'text-eve-danger'

  return (
    <div className="card">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-eve-muted text-[10px] uppercase tracking-widest font-medium">
            Balance — timeline
          </div>
          <div className="font-display text-2xl sm:text-3xl font-bold text-eve-text mt-1">
            {data ? formatISK(data.end_balance, 0) : '—'}
            <span className="text-base ml-2 font-mono text-eve-muted">ISK</span>
          </div>
          {data && (
            <div className={`text-sm font-mono mt-1 ${deltaColor}`}>
              {data.change >= 0 ? '+' : ''}
              {formatISKShort(data.change)} ({data.change_pct >= 0 ? '+' : ''}
              {data.change_pct.toFixed(2)}%) en {data.days}d
              <span className="text-eve-muted ml-2 font-sans italic">
                · start {formatISKShort(data.start_balance)}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Toggle 7d / 30d */}
          <div className="flex gap-1 bg-eve-panel border border-eve-border rounded-md p-0.5">
            {RANGES.map((r) => (
              <button
                key={r.days}
                onClick={() => setRange(r.days)}
                className={`px-2.5 py-0.5 text-xs font-mono rounded transition-colors ${
                  range === r.days
                    ? 'bg-eve-accent text-eve-bg font-semibold'
                    : 'text-eve-muted hover:text-eve-text'
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <LastUpdated timestamp={dataUpdatedAt} label="" />
          <RefreshButton onClick={bump} isFetching={isFetching} />
        </div>
      </div>

      <div className="-mx-1">
        {isLoading ? (
          <LoadingSkeleton variant="default" />
        ) : (
          <WalletSparkline points={data?.points ?? []} />
        )}
      </div>

      {/* Footer con metadata — útil para auditar de dónde sale el dato */}
      {data && data.points.length > 0 && (
        <div className="text-[10px] text-eve-muted font-mono mt-2 flex items-center gap-3 flex-wrap">
          <span>
            {data.points.length} días · {data.start_day} → {data.end_day}
          </span>
          <span>·</span>
          <span>{data.pages_walked} páginas de journal consultadas</span>
          <span>·</span>
          <span>
            min {formatISKShort(Math.min(...data.points.map((p) => p.balance)))} · max{' '}
            {formatISKShort(Math.max(...data.points.map((p) => p.balance)))}
          </span>
        </div>
      )}
    </div>
  )
}